import { createApi } from "@reduxjs/toolkit/query/react";
import type { Draft } from "immer";
import { ApiEndpoints, baseQueryWithAuth } from "../../core";
import { toast } from "../../core/toast";
import { getToken } from "../../utils/auth";

// 1. Types
// DELETE the first ThemeColors and keep only this merged version:
export interface IGradient {
  from: { color: string; opacity: number };
  to: { color: string; opacity: number };
}

export interface IBranding {
  name?: string | null;
  logo: string | null;
  visibility: { device: boolean; user: boolean };
}

export interface IAssets {
  banners: { device: string | null; user: string | null };
}

export interface IColors {
  primary: string | null;
  secondary: string | null;
  supporting: string | null;
  icon: string | null;
  iconBackground: string | null;
  appBackground: string | null;
  card: string | null;
  popup: string | null;
  headingText: string | null;
  bodyText: string | null;
  mutedText: string | null;
  errorText: string | null;
}

export interface IGradientColors {
  icon: IGradient | null;
  iconBackground: IGradient | null;
  deviceBackground: IGradient | null;
  card: IGradient | null;
  popup: IGradient | null;
  progressBar: IGradient | null;
  userBackground: IGradient | null;
}

export interface IStrokeColors {
  card: string | null;
  popup: string | null;
  progressBar: string | null;
}

export interface IThemeMode {
  branding: IBranding;
  colors: IColors;
  gradients: IGradientColors | null;
  strokes: IStrokeColors | null;
  assets: IAssets;
}

export interface ThemeSettings {
  _id: string;
  name: string;
  organisation: string;
  mode: "LIGHT" | "DARK" | "BOTH";
  defaultMode: "LIGHT" | "DARK" | "AUTO";
  lightTheme: IThemeMode | null;
  darkTheme: IThemeMode | null;
  status: "ACTIVE" | "INACTIVE" | "Active" | "Inactive";
}

export interface ThemeDocument extends ThemeSettings {
  _id: string;
  access: string;
  subscriber: string;
  createdAt: string; // ISO Date string from DB
  updatedAt: string; // ISO Date string from DB
}

export interface Theme {
  _id: string;
  name: string;
  access: "ORGANISATION" | "SUBSCRIBER" | string; // Based on your "ORGANISATION" value
  type: "CUSTOM" | "DEFAULT" | string;
  organisation: string; // This holds the ID "69e1f240..."
  subscriber: string;
  mode: "BOTH" | "DARK" | "LIGHT"; 
  defaultMode: "DARK" | "LIGHT" | "AUTO";
  status: "Active" | "Inactive" | string;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  // Theme objects - using the interfaces you defined earlier in the slice
  lightTheme: IThemeMode | null;
  darkTheme: IThemeMode | null;
  isDefault?: boolean;
}





// 2. API Slice
export const themeApi = createApi({
  reducerPath: "themeApi",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["Theme", "Themes"],

  endpoints: (builder) => ({
    
    // GET ALL
   getThemesByOrg: builder.query<Theme[], string>({
  query: (organisationId) => ({
    url: `/subscriber-service/api/v1/theme/organisation/${organisationId}`,
    method: "GET",
  }),
  transformResponse: (res: any) => {
    return Array.isArray(res?.results) ? res.results : res?.data || res || [];
  },
  providesTags: ["Themes"],
}),

    // GET SINGLE
    getThemeById: builder.query<ThemeSettings, string>({
      query: (themeId) => ({
        url: `${ApiEndpoints.theme}/${themeId}`,
        method: "GET",
      }),
      transformResponse: (res: any) => res?.result ?? res?.data ?? res,
      providesTags: (_result, _error, themeId) => [
        { type: "Theme", id: themeId },
      ],
    }),

    // CREATE
    createTheme: builder.mutation<any, Partial<ThemeSettings>>({
      query: (data) => ({
        url: `${ApiEndpoints.theme}/subscriber`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Themes"],
    }),

    // UPDATE
    updateTheme: builder.mutation<any, { themeId: string; data: Partial<ThemeSettings> }>({
      query: ({ themeId, data }) => ({
        url: `${ApiEndpoints.theme}/${themeId}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _error, { themeId }) => [
        "Themes",
        { type: "Theme", id: themeId },
      ],
    }),

    // DELETE
   deleteTheme: builder.mutation<any, string>({ // 👈 Changed argument type to 'string'
  query: (themeId) => ({
    url: `${ApiEndpoints.theme}/${themeId}`,
    method: "DELETE",
  }),
  invalidatesTags: ["Themes"],
  async onQueryStarted(themeId, { dispatch, queryFulfilled }) {
    // Optimistic Delete
    const patchResult = dispatch(
      themeApi.util.updateQueryData("getThemesByOrg", undefined as any, (draft) => {
        // Now themeId is properly recognized as a string
        return draft.filter((t: any) => t._id !== themeId); 
      })
    );
    
    try {
      await queryFulfilled;
      toast.success("Theme deleted successfully");
    } catch (error: any) {
      toast.error(error?.error?.data?.message || "Failed to delete theme");
      patchResult.undo();
    }
  },
}),

    // TOGGLE STATUS (Active/Inactive)
    changeThemeStatus: builder.mutation<any, { themeId: string; status: "ACTIVE" | "INACTIVE" }>({
      query: ({ themeId, status }) => ({
        url: `/subscriber-service/api/v1/theme/change-status/${themeId}`, // Update to your actual endpoint
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: (_result, _error, { themeId }) => [
        "Themes",
        { type: "Theme", id: themeId },
      ],
      async onQueryStarted({ themeId, status }, { dispatch, queryFulfilled }) {
        // Optimistic Status Update in List
        const patchList = dispatch(
          themeApi.util.updateQueryData("getThemesByOrg", undefined as any, (draft) => {
            const theme = draft.find((t) => t._id === themeId);
            if (theme) theme.status = status;
          })
        );

        // Optimistic Status Update in Detail View
        const patchDetail = dispatch(
          themeApi.util.updateQueryData("getThemeById", themeId, (draft) => {
            if (draft) draft.status = status;
          })
        );

        try {
          await queryFulfilled;
          toast.success(`Theme marked as ${status}`);
        } catch (error: any) {
          toast.error(error?.error?.data?.message || "Failed to update status");
          patchList.undo();
          patchDetail.undo();
        }
      },
    }),

    // GET DEFAULT THEME
    getDefaultTheme: builder.query<Theme, void>({
      query: () => ({
        // Adjust this if you use your ApiEndpoints constants instead
        url: `/subscriber-service/api/v1/theme/default`, 
        method: "GET",
      }),
      transformResponse: (res: any) => {
        // Extracts the theme object based on standard Express/Mongoose responses
        return res?.result ?? res?.data ?? res;
      },
      // Tagging it with "Themes" ensures this cache automatically refreshes 
      // if a different theme is saved/toggled to active!
      providesTags: ["Themes"], 
    }),


uploadThemeLogo: builder.mutation<
  any,
  {
    file: File | Blob;
    onUploadProgress?: (progress: number) => void;
  }
>({
  async queryFn(arg) {
    const { file, onUploadProgress } = arg;

    const form = new FormData();

    form.append(
      "image",
      file,
      (file as File)?.name || "theme-logo.png"
    );

    return await new Promise<any>((resolve) => {
      try {
        const xhr = new XMLHttpRequest();

        const base = (ApiEndpoints as any).serverURL || "";

        const url = `${base}/file-service/api/v1/file/theme/subscriber`;

        xhr.open("POST", url);

        const token = getToken();

        if (token) {
          xhr.setRequestHeader(
            "Authorization",
            `Bearer ${token}`
          );
        }

        if (
          xhr.upload &&
          typeof onUploadProgress === "function"
        ) {
          xhr.upload.onprogress = (e: ProgressEvent) => {
            if (e.lengthComputable) {
              const percent = Math.round(
                (e.loaded / e.total) * 100
              );

              try {
                onUploadProgress(percent);
              } catch {
                //
              }
            }
          };
        }

        xhr.onreadystatechange = () => {
          if (xhr.readyState !== 4) return;

          const ct =
            xhr.getResponseHeader("content-type") || "";

          let parsed: any = null;

          try {
            parsed = ct.includes("application/json")
              ? JSON.parse(xhr.responseText)
              : xhr.responseText;
          } catch {
            parsed = xhr.responseText;
          }

          if (xhr.status >= 200 && xhr.status < 300) {
            toast.success("Theme logo uploaded");

            resolve({ data: parsed });
          } else {
            toast.error(
              parsed?.message || "Upload failed"
            );

            resolve({
              error: {
                status: xhr.status,
                data: parsed,
              },
            });
          }
        };

        xhr.onerror = () => {
          toast.error("Upload network error");

          resolve({
            error: {
              status: "NETWORK_ERROR",
              data: null,
            },
          });
        };

        xhr.send(form);
      } catch (err: any) {
        toast.error("Upload error");

        resolve({
          error: {
            status: "XHR_ERROR",
            data: err?.message ?? err,
          },
        });
      }
    });
  },
}),

  }),
});

// 3. Export Hooks
export const {
  useGetThemesByOrgQuery,
  useLazyGetThemesByOrgQuery,
  useGetThemeByIdQuery,
  useCreateThemeMutation,
  useUpdateThemeMutation,
  useDeleteThemeMutation,
  useChangeThemeStatusMutation,
  useGetDefaultThemeQuery,
  useUploadThemeLogoMutation
} = themeApi;